import { inject, Injectable } from '@angular/core';

import { Action, createSelector, Store } from '@ngrx/store';
import { Observable, take } from 'rxjs';

import { filterBoolean } from '@recruitee/common';
import { DevFlag, DevFlagSelectors } from '@recruitee/dev-flags/data';
import { OfferItem, TalentPool, TalentPoolBrief, TalentPoolStatus } from '@recruitee/http/types';
import { memoizeSelector } from '@recruitee/ngrx';
import {
  TalentPoolItemsActions,
  TalentPoolItemsSelectors,
  TalentPoolsBriefActions,
  TalentPoolsBriefSelectors,
} from '@recruitee/offers/data';

import {
  TalentPoolsNewBriefActions,
  TalentPoolsNewBriefSelectors,
} from './talent-pool/talent-pool-brief.store';
import { TalentPoolsNewActions, TalentPoolsNewSelectors } from './talent-pool/talent-pool.store';
import { TalentPoolsScope } from './talent-pool/talent-pool.types';

const featureFlag = DevFlagSelectors.isEnabled(DevFlag.NewTalentPools);
const toNewTypeMap = (oldEntity: OfferItem) =>
  <TalentPool>{
    ...oldEntity,
    department: oldEntity.department || null,
    example: oldEntity.example || false,
  };

const getScope = (archived = false) =>
  archived ? TalentPoolsScope.Archived : TalentPoolsScope.Default;
const byArchivedFilter = (archived: boolean) => (entity: TalentPool) =>
  archived
    ? entity.status === TalentPoolStatus.Archived
    : entity.status === TalentPoolStatus.Default;

const oldEntities = createSelector(TalentPoolItemsSelectors.allEntities, entities =>
  entities.map(toNewTypeMap),
);

const oldEntitiesBrief = createSelector(
  TalentPoolsBriefSelectors.allEntities,
  entities => <TalentPoolBrief[]>entities,
);

const collection = createSelector(
  featureFlag,
  oldEntities,
  TalentPoolsNewSelectors.allEntities,
  (flag, oldEntities, newEntities) =>
    (flag ? newEntities : oldEntities).sort((a, b) => b.position - a.position),
);
const collectionBrief = createSelector(
  featureFlag,
  oldEntitiesBrief,
  TalentPoolsNewBriefSelectors.allEntities,
  (flag, oldEntities, newEntities) =>
    (flag ? newEntities : oldEntities).sort((a, b) => b.position - a.position),
);
const collectionScoped = memoizeSelector((archived: boolean = false) =>
  createSelector(collection, entities => entities.filter(byArchivedFilter(archived))),
);
const collectionBriefScoped = memoizeSelector((archived: boolean) =>
  createSelector(collectionBrief, entities => entities.filter(byArchivedFilter(archived))),
);
const metaScoped = memoizeSelector((archived = false) =>
  createSelector(
    featureFlag,
    TalentPoolItemsSelectors.meta({ scope: getScope(archived) }),
    TalentPoolsNewSelectors.meta({ scope: getScope(archived) }),
    (flag, oldMeta, newMeta) => (flag ? newMeta : oldMeta),
  ),
);
const byIdOrSlug = memoizeSelector((identifier: number | string) =>
  createSelector(collection, entities =>
    entities.find(entity => entity.id === identifier || entity.slug === identifier),
  ),
);

const injectFeatureFlag = () => {
  let enabled = false;
  inject(Store)
    .select(DevFlagSelectors.isEnabled(DevFlag.NewTalentPools))
    .pipe(take(1))
    .subscribe(flag => (enabled = flag));
  return enabled;
};

@Injectable({
  providedIn: 'root',
})
export class TalentPoolsDataService {
  private store = inject(Store);
  private newTalentPools = injectFeatureFlag();

  public collection$(archived: boolean = false): Observable<TalentPool[]> {
    return this.store.select(collectionScoped(archived)).pipe(filterBoolean);
  }

  public collectionBrief$(archived: boolean = false): Observable<TalentPoolBrief[]> {
    return this.store.select(collectionBriefScoped(archived)).pipe(filterBoolean);
  }

  public meta$(archived = false) {
    return this.store.select(metaScoped(archived));
  }

  public byIdOrSlugFromCollection$(idOrSlug: number | string) {
    return this.store.select(byIdOrSlug(idOrSlug));
  }

  public fetchCollection(archived: boolean = false) {
    let action: Action;
    if (this.newTalentPools) {
      const scope: TalentPoolsScope = archived
        ? TalentPoolsScope.Archived
        : TalentPoolsScope.Default;
      action = TalentPoolsNewActions.fetchCollection({ payload: { scope } });
    } else {
      action = archived
        ? TalentPoolItemsActions.fetchArchivedCollection()
        : TalentPoolItemsActions.fetchActiveCollection();
    }
    this.store.dispatch(action);
  }

  public fetchBriefCollection() {
    const action = this.newTalentPools
      ? TalentPoolsNewBriefActions.fetchCollection()
      : TalentPoolsBriefActions.fetchCollection();
    this.store.dispatch(action);
  }
}
